import { useCallback, useEffect, useRef, useState } from 'react';

import { PHOTO_ID_EMAIL_STATUS_POLLING } from 'appConstants';
import { useCustomers } from 'contexts/Customers';

export type PhotoIdEmailStatus =
  | 'idle'
  | 'checking'
  | 'sending'
  | 'sent'
  | 'noMyFarm'
  | 'alreadySent'
  | 'error';

interface IUsePhotoIdEmailStatusParams {
  enabled: boolean;
  customerReference?: string;
  representativeReference?: string;
}

// Maps a require-email-status response to a resolved (non-transient) UI state, or null while still in progress.
const resolveStatus = (data: { status?: string; templateKey?: string } | undefined) => {
  if (data?.status === 'sent')
    return data.templateKey === 'photoIdCustomerServiceMyFarmAcc' ? 'noMyFarm' : 'sent';
  if (data?.status === 'failed') return 'error';
  return null;
};

export default function usePhotoIdEmailStatus({
  enabled,
  customerReference,
  representativeReference,
}: IUsePhotoIdEmailStatusParams) {
  const {
    actions: { sendPhotoIdEmailLink, getPhotoIdEmailStatus },
  } = useCustomers();
  const [emailStatus, setEmailStatus] = useState<PhotoIdEmailStatus>('idle');
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const pollDeadlineRef = useRef(0);

  const clearPoll = useCallback(() => {
    clearTimeout(pollTimeoutRef.current);
  }, []);

  const pollStatus = useCallback(async () => {
    if (!customerReference) return;

    const { data, error } = await getPhotoIdEmailStatus(customerReference);
    const resolved = !error ? resolveStatus(data) : null;

    if (resolved) {
      setEmailStatus(resolved);
      return;
    }

    if (Date.now() >= pollDeadlineRef.current) {
      setEmailStatus('error');
      return;
    }

    pollTimeoutRef.current = setTimeout(pollStatus, PHOTO_ID_EMAIL_STATUS_POLLING.INTERVAL_MS);
  }, [customerReference, getPhotoIdEmailStatus]);

  // Seed the initial state once, when the modal opens for a customer.
  useEffect(() => {
    if (!enabled || !customerReference) return undefined;

    let isCancelled = false;
    setEmailStatus('checking');

    (async () => {
      const { data, error } = await getPhotoIdEmailStatus(customerReference);
      if (isCancelled) return;
      setEmailStatus(!error && data?.status === 'sent' ? 'alreadySent' : 'idle');
    })();

    return () => {
      isCancelled = true;
      clearPoll();
    };
  }, [enabled, customerReference, clearPoll, getPhotoIdEmailStatus]);

  useEffect(() => clearPoll, [clearPoll]);

  const sendEmail = useCallback(async () => {
    if (!customerReference || !representativeReference) return;

    setEmailStatus('sending');
    const { error } = await sendPhotoIdEmailLink(customerReference, representativeReference);

    if (error) {
      setEmailStatus('error');
      return;
    }

    pollDeadlineRef.current = Date.now() + PHOTO_ID_EMAIL_STATUS_POLLING.TIMEOUT_MS;
    await pollStatus();
  }, [customerReference, representativeReference, sendPhotoIdEmailLink, pollStatus]);

  return { emailStatus, sendEmail };
}
